import api from "./api";

/**
 * Service central do módulo Beleza — V20 produção UX senior.
 * Fluxo de tela: Agenda → Caixa. Atendimento permanece apenas como motor/status interno.
 */
export const unwrap = (res) => {
  if (!res) return res;
  return res?.data?.data ?? res?.data?.Data ?? res?.data ?? res;
};

export const unwrapResponse = unwrap;

export const asArray = (payload) => {
  const data = unwrap(payload);
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.Items)) return data.Items;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.Data)) return data.Data;
  if (Array.isArray(data?.data?.items)) return data.data.items;
  if (Array.isArray(data?.data?.Items)) return data.data.Items;
  if (Array.isArray(data?.resultado)) return data.resultado;
  if (Array.isArray(data?.Resultado)) return data.Resultado;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.Rows)) return data.Rows;
  return [];
};

export const asPagedItems = asArray;

export const asPagedResult = (payload) => {
  const data = unwrap(payload);
  const items = asArray(data);
  return {
    items,
    total: data?.total ?? data?.Total ?? data?.totalCount ?? data?.TotalCount ?? data?.count ?? data?.Count ?? items.length,
    page: data?.page ?? data?.Page ?? data?.pagina ?? data?.Pagina ?? 1,
    pageSize: data?.pageSize ?? data?.PageSize ?? data?.tamanhoPagina ?? data?.TamanhoPagina ?? items.length,
  };
};

export const asId = (value) => {
  const id = value?.id ?? value?.Id ?? value;
  return id ? String(id) : "";
};

export const buildQuery = (params = {}) => {
  const clean = {};
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "" && !(Array.isArray(value) && value.length === 0)) {
      clean[key] = value;
    }
  });
  return clean;
};

const request = ({ method = "get", url, params, data, headers }) =>
  api({ method, url, params: buildQuery(params), data, headers }).then(unwrap);

const get = (url, params) => request({ method: "get", url, params });
const post = (url, payload) => request({ method: "post", url, data: payload });
const put = (url, payload) => request({ method: "put", url, data: payload });
const del = (url, params) => request({ method: "delete", url, params });

// O navegador/Axios define o boundary correto do multipart; não fixar o Content-Type.
const postForm = (url, formData) => request({ method: "post", url, data: formData });

const salvarPorId = (baseUrl) => (payload) => {
  const id = payload?.id || payload?.Id;
  return id ? put(`${baseUrl}/${id}`, payload) : post(baseUrl, payload);
};

const crud = (baseUrl) => ({
  listar: (params) => get(baseUrl, params),
  obter: (id) => get(`${baseUrl}/${id}`),
  criar: (payload) => post(baseUrl, payload),
  atualizar: (id, payload) => put(`${baseUrl}/${id}`, payload),
  salvar: salvarPorId(baseUrl),
  excluir: (id) => del(`${baseUrl}/${id}`),
});

const uploadFoto = (url) => (id, arquivo) => {
  const form = new FormData();
  form.append("arquivo", arquivo);
  return postForm(`${url}/${id}/foto`, form);
};

const removerFoto = (url) => (id) => del(`${url}/${id}/foto`);

const normalizeFinanceiroOpcoes = (payload) => {
  const data = unwrap(payload) || {};
  return {
    formasPagamento: asArray(data.formasPagamento ?? data.FormasPagamento ?? data.formas ?? data.Formas),
    operacoes: asArray(data.operacoes ?? data.Operacoes),
    contasFinanceiras: asArray(data.contasFinanceiras ?? data.ContasFinanceiras ?? data.contas ?? data.Contas),
    raw: data,
  };
};

const normalizeCaixaDashboard = (payload) => {
  const data = unwrap(payload) || {};
  const status = String(data.status ?? data.Status ?? "").toLowerCase();
  return {
    caixaAberto: Boolean(data.caixaAberto ?? data.CaixaAberto ?? status === "aberto"),
    caixaSessaoId: data.caixaSessaoId ?? data.CaixaSessaoId ?? data.id ?? data.Id,
    valorAbertura: data.valorAbertura ?? data.ValorAbertura ?? 0,
    valorCalculado: data.valorCalculado ?? data.ValorCalculado ?? 0,
    atendimentosPendentes: data.atendimentosPendentes ?? data.AtendimentosPendentes ?? 0,
    totalPendente: data.totalPendente ?? data.TotalPendente ?? 0,
    recebimentosHoje: data.recebimentosHoje ?? data.RecebimentosHoje ?? 0,
    totalRecebidoHoje: data.totalRecebidoHoje ?? data.TotalRecebidoHoje ?? 0,
    raw: data,
  };
};

const BelezaService = {
  unwrap,
  unwrapResponse,
  asArray,
  asPagedItems,
  asPagedResult,
  asId,
  buildQuery,

  // Agenda produção
  agendaSemana: (params) => get("/api/beleza/agenda/senior/semana", params),
  agendaDisponibilidadeLateral: (params) => get("/api/beleza/agenda/senior/disponibilidade-lateral", params),
  criarAgendamento: (payload) => post("/api/beleza/agenda/senior/agendamentos", payload),
  confirmarAgendamento: (id) => post(`/api/beleza/agenda/senior/${id}/confirmar`),
  iniciarAtendimentoAgendamento: (id) => post(`/api/beleza/agenda/senior/${id}/iniciar-atendimento`),
  cancelarAgendamento: (id, payload = {}) => post(`/api/beleza/agenda/senior/${id}/cancelar`, payload),

  // Atendimento interno — usado pela Agenda/Caixa, não como tela principal
  listarAtendimentosSenior: (params) => get("/api/beleza/atendimentos/senior", params),
  obterAtendimentoSenior: (id) => get(`/api/beleza/atendimentos/senior/${id}`),
  adicionarServicoAtendimento: (id, payload) => post(`/api/beleza/atendimentos/senior/${id}/servicos`, payload),
  adicionarProdutoAtendimento: (id, payload) => post(`/api/beleza/atendimentos/senior/${id}/produtos`, payload),
  removerServicoAtendimento: (id, itemId, autorizacao = null) => request({ method: "delete", url: `/api/beleza/atendimentos/senior/${id}/servicos/${itemId}`, data: autorizacao }),
  removerProdutoAtendimento: (id, itemId, autorizacao = null) => request({ method: "delete", url: `/api/beleza/atendimentos/senior/${id}/produtos/${itemId}`, data: autorizacao }),
  aplicarDescontoAtendimento: (id, payload) => post(`/api/beleza/atendimentos/senior/${id}/desconto`, payload),
  finalizarAtendimento: (id, payload = {}) => post(`/api/beleza/atendimentos/senior/${id}/finalizar`, payload),
  cancelarAtendimento: (id, payload = {}) => post(`/api/beleza/atendimentos/senior/${id}/cancelar`, payload),

  // Caixa PDV produção
  caixaConfiguracao: () => get("/api/beleza/gestao360/caixa-config"),
  caixaDashboard: () => get("/api/beleza/caixa/pdv-producao/dashboard").then(normalizeCaixaDashboard),
  caixaCupomIdentificacao: () => get("/api/beleza/caixa/pdv-producao/cupom-identificacao"),
  caixaPendentes: (params = {}) => get("/api/beleza/caixa/pdv-producao/pendentes", params),
  caixaCatalogo: (params) => get("/api/beleza/caixa/pdv-producao/catalogo", params),
  caixaFinanceiroOpcoes: () => get("/api/beleza/caixa/pdv-producao/financeiro-opcoes").then(normalizeFinanceiroOpcoes),
  caixaFormasPagamento: (params = {}) => get("/api/beleza/caixa/pdv-producao/formas-pagamento", params),
  caixaValidarVoucher: (params = {}) => get("/api/beleza/caixa/pdv-producao/vouchers/validar", params),
  caixaPacotesCliente: (clienteId, params = {}) => get(`/api/beleza/caixa/pdv-producao/clientes/${clienteId}/pacotes-ativos`, params),
  caixaDetalheAtendimento: (id) => get(`/api/beleza/caixa/pdv-producao/atendimentos/${id}/detalhe`),
  caixaReceber: (payload) => post("/api/beleza/caixa/pdv-producao/receber", payload),
  abrirCaixa: (payload) => post("/api/beleza/caixa/pdv-producao/abrir", payload),
  fecharCaixa: (payload) => post("/api/beleza/caixa/pdv-producao/fechar", payload),
  caixaAtual: () => get("/api/beleza/caixa/pdv-producao/dashboard"),
  caixaMovimentos: (caixaSessaoId, params) => get(`/api/beleza/caixa/pdv-producao/${caixaSessaoId}/movimentos`, params),
  caixaSangria: (payload) => post("/api/beleza/caixa/pdv-producao/sangria", payload),
  caixaSuprimento: (payload) => post("/api/beleza/caixa/pdv-producao/suprimento", payload),
  caixaOperacaoManual: (payload) => post("/api/beleza/caixa/pdv-producao/operacoes", payload),

  listarSetores: (params) => get("/api/beleza/setores", params),
  listarColaboradores: (params) => get("/api/beleza/colaboradores", params),
  listarClientes: (params) => get("/api/beleza/clientes", params),
  listarServicos: (params) => get("/api/beleza/servicos", params),
  listarProdutos: (params) => get("/api/beleza/produtos", params),

  setores: crud("/api/beleza/setores"),
  servicos: { ...crud("/api/beleza/servicos"), uploadFoto: uploadFoto("/api/beleza/servicos"), removerFoto: removerFoto("/api/beleza/servicos") },
  clientes: {
    ...crud("/api/beleza/clientes"),
    configuracaoLocal: () => get("/api/beleza/clientes/configuracao-local"),
    pacotesResumo: (id) => get(`/api/beleza/clientes/${id}/pacotes-resumo`),
    uploadFoto: uploadFoto("/api/beleza/clientes"),
    removerFoto: removerFoto("/api/beleza/clientes"),
    dependentes: (clienteId) => get(`/api/beleza/clientes/${clienteId}/dependentes`),
    salvarDependente: (clienteId, payload) => {
      const id = payload?.id || payload?.Id;
      return id ? put(`/api/beleza/clientes/${clienteId}/dependentes/${id}`, payload) : post(`/api/beleza/clientes/${clienteId}/dependentes`, payload);
    },
    excluirDependente: (clienteId, dependenteId) => del(`/api/beleza/clientes/${clienteId}/dependentes/${dependenteId}`),
  },
  colaboradores: {
    ...crud("/api/beleza/colaboradores"),
    uploadFoto: uploadFoto("/api/beleza/colaboradores"),
    removerFoto: removerFoto("/api/beleza/colaboradores"),
    disponibilidades: (id) => get(`/api/beleza/colaboradores/${id}/disponibilidades`),
    salvarGradeDisponibilidade: (id, disponibilidades) => post(`/api/beleza/colaboradores/${id}/disponibilidades/grade`, { disponibilidades }),
    convidarPwa: (id, payload = {}) => post(`/api/beleza/colaboradores/${id}/convite-pwa`, payload),
    vincularUsuarioExistentePwa: (id) => post(`/api/beleza/colaboradores/${id}/vincular-usuario-pwa`, {}),
  },
  produtos: {
    ...crud("/api/beleza/produtos"),
    uploadFoto: uploadFoto("/api/beleza/produtos"),
    removerFoto: removerFoto("/api/beleza/produtos"),
    categorias: {
      ...crud("/api/beleza/produtos/categorias"),
      produtosVinculados: (id, params) => get(`/api/beleza/produtos/categorias/${id}/produtos`, params),
    },
  },
  pacotes: {
    ...crud("/api/beleza/pacotes"),
    uploadFoto: uploadFoto("/api/beleza/pacotes"),
    removerFoto: removerFoto("/api/beleza/pacotes"),
    servicos: (id) => get(`/api/beleza/pacotes/${id}/servicos`),
    salvarServicos: (id, payload) => post(`/api/beleza/pacotes/${id}/servicos`, payload),
    removerServico: (id, servicoPacoteId) => del(`/api/beleza/pacotes/${id}/servicos/${servicoPacoteId}`),
  },
  valesPresente: {
    ...crud("/api/beleza/vales-presente"),
    lotes: (params) => get("/api/beleza/vales-presente/lotes", params),
    criarLote: (payload) => post("/api/beleza/vales-presente/lote", payload),
    uploadFoto: uploadFoto("/api/beleza/vales-presente"),
    removerFoto: removerFoto("/api/beleza/vales-presente"),
    validarPublico: (payload) => post("/api/beleza/vales-presente/publico/validar", payload),
    ativarPublico: (payload) => post("/api/beleza/vales-presente/publico/ativar", payload),
  },

  agenda: {
    listar: (params) => BelezaService.agendaSemana(params),
    disponibilidade: (params) => BelezaService.agendaDisponibilidadeLateral(params),
    criar: (payload) => BelezaService.criarAgendamento(payload),
    confirmar: (id) => BelezaService.confirmarAgendamento(id),
    iniciar: (id) => BelezaService.iniciarAtendimentoAgendamento(id),
    cancelar: (id, payload = {}) => BelezaService.cancelarAgendamento(id, payload),
  },
  atendimentos: {
    listar: (params) => BelezaService.listarAtendimentosSenior(params),
    obter: (id) => BelezaService.obterAtendimentoSenior(id),
    adicionarServico: (id, payload) => BelezaService.adicionarServicoAtendimento(id, payload),
    adicionarProduto: (id, payload) => BelezaService.adicionarProdutoAtendimento(id, payload),
    finalizar: (id, payload) => BelezaService.finalizarAtendimento(id, payload),
    cancelar: (id, payload) => BelezaService.cancelarAtendimento(id, payload),
  },
  caixa: {
    dashboard: () => BelezaService.caixaDashboard(),
    pendentes: (params) => BelezaService.caixaPendentes(params),
    catalogo: (params) => BelezaService.caixaCatalogo(params),
    financeiroOpcoes: () => BelezaService.caixaFinanceiroOpcoes(),
    formasPagamento: (params) => BelezaService.caixaFormasPagamento(params),
    validarVoucher: (params) => BelezaService.caixaValidarVoucher(params),
    pacotesCliente: (clienteId, params) => BelezaService.caixaPacotesCliente(clienteId, params),
    detalheAtendimento: (id) => BelezaService.caixaDetalheAtendimento(id),
    receber: (payload) => BelezaService.caixaReceber(payload),
    abrir: (payload) => BelezaService.abrirCaixa(payload),
    fechar: (payload) => BelezaService.fecharCaixa(payload),
    atual: () => BelezaService.caixaAtual(),
    movimentos: (id, params) => BelezaService.caixaMovimentos(id, params),
    sangria: (payload) => BelezaService.caixaSangria(payload),
    suprimento: (payload) => BelezaService.caixaSuprimento(payload),
    operacaoManual: (payload) => BelezaService.caixaOperacaoManual(payload),
  },
  relatorios: {
    resumoExecutivo: (params) => get("/api/beleza/relatorios/resumo-executivo", params),
    financeiro: (params) => get("/api/beleza/relatorios/financeiro", params),
    produtos: (params) => get("/api/beleza/relatorios/produtos", params),
    clientes: (params) => get("/api/beleza/relatorios/clientes", params),
  },
  meta: {
    config: () => get("/api/beleza/whatsapp-meta/config"),
    contas: () => get("/api/beleza/whatsapp-meta/contas"),
    outbox: (params) => get("/api/beleza/whatsapp-meta/outbox", params),
    finalizarEmbeddedSignup: (payload) => post("/api/beleza/whatsapp-meta/embedded-signup/finalizar", payload),
    agente: (contaId, ativo) => post(`/api/beleza/whatsapp-meta/contas/${contaId}/agente`, { ativo }),
    enviarTexto: (payload) => post("/api/beleza/whatsapp-meta/enviar-texto", payload),
  },
  agente: {
    processarOutbox: () => post("/api/beleza/agente/processar-outbox"),
    processarLembretes: () => post("/api/beleza/agente/processar-lembretes"),
  },
  saas: {
    planoPublico: () => get("/api/beleza/saas/plano-publico"),
    criarCheckout: (payload) => post("/api/beleza/saas/checkout", payload),
  },
  financeiro: {
    formasPagamentoPdv: (params) => get("/api/beleza/caixa/pdv-producao/formas-pagamento", params),
    operacoesPdv: () => get("/api/beleza/caixa/pdv-producao/operacoes"),
  },
};

export default BelezaService;
